/**
 * exp-f-equal-form (US-AIF.P2-0253c · @UC9433 · @gate money,ai) — FORM EXP-F-EQUAL.
 *
 * USECASE (real_scenarios.md §1.1 nhóm A): "Chi từ quỹ, chia đều gánh nặng cho tất cả".
 * Ví dụ chuẩn: quỹ trả 400 tiền nước tháng này → mỗi người "gánh" 100 (4 người).
 *
 * NGỮ NGHĨA TIỀN (source `fund_flow_allocated` — NỀN đã dựng ở 0253):
 *  - QUỸ trả 100% CASH (CREDIT CASH = amountMinor TOÀN BỘ) — không thành viên nào ứng tiền.
 *  - **ZERO DebtEntry**: không ai nợ ai (quỹ đã trả). Đây là điểm PHÂN BIỆT với `peer`/`mixed`.
 *  - `ExpenseParticipant.shareMinor` chia ĐỀU để MINH BẠCH ai gánh bao nhiêu (Σshare = amountMinor).
 *  - `payerMemberId` = NULL, `fundBearsAllShares` = true, `fundPortionMinor` = 0 (service tự cân bằng).
 *
 * KHÁC EXP-F-NOALLOC: NOALLOC = quỹ trả, KHÔNG chia cho ai (participants=[], source=fund_flow).
 * EXP-F-EQUAL = quỹ trả, CHIA ĐỀU gánh nặng (participants=N, source=fund_flow_allocated). Cả hai đều
 * KHÔNG sinh nợ — khác nhau ở chỗ có breakdown gánh nặng hay không.
 *
 * HÀM THUẦN (DP-3): KHÔNG DB, KHÔNG LLM, KHÔNG Date.now. Money = string minor VND (B1 — no-float, >0).
 * fund_picker + multi_choice(bearers) options do SERVICE nạp (quỹ/thành viên THẬT) — domain chỉ validate
 * value ∈ options (chống IDOR ở LỚP domain; service re-verify RBAC/tenant THẬT lúc confirm).
 *
 * 🔴 KHÔNG TÍNH TIỀN Ở ĐÂY: domain form chỉ chốt AI GÁNH (tập bearer). Chia đều bao nhiêu mỗi người do
 * `ExpenseService.computeShares` (split-engine `equal` + `distributeWithRemainder`) tính — MỘT NGUỒN
 * công thức tiền. Form KHÔNG gửi shareMinor/fundPortionMinor (contract `refineExpenseSplitInvariants`
 * CẤM với fund_flow_allocated — service tự cân bằng).
 */
import { DomainError } from "../errors.js";
import {
  validateFieldValue,
  type SlotFieldSpec,
  type SlotOption,
  type FormState,
} from "./form-slot-filling.js";
import {
  EXP_ADHOC_NAME_MAX_LENGTH,
  EXP_ADHOC_PG_BIGINT_MAX,
  EXP_SOURCE_ALLOCATED,
  type ExpFormSplit,
} from "./exp-adhoc-form.js";

/** intentCode EXP-F-EQUAL (chi từ quỹ, chia ĐỀU gánh nặng cho tất cả). Khớp catalog + router EXACT. */
export const EXP_F_EQUAL_INTENT_CODE = "EXP-F-EQUAL";

/** specVersion form EXP-F-EQUAL (tăng khi đổi chuỗi field — session lưu để phát hiện drift). */
export const EXP_F_EQUAL_SPEC_VERSION = 1;

/** Key field chọn NGƯỜI GÁNH (multi_choice trên thành viên THẬT của group chứa quỹ). */
export const EXP_F_EQUAL_BEARERS_KEY = "bearers";

/**
 * Thứ tự field EXP-F-EQUAL (tất-định — thứ tự hỏi). `bearers` SAU `fundId` vì options thành viên chỉ
 * hydrate được KHI đã biết quỹ (step-wise — mirror COL-PER-MEMBER member_picker).
 */
export const EXP_F_EQUAL_FIELD_ORDER = ["fundId", "name", "amount", EXP_F_EQUAL_BEARERS_KEY] as const;

/** Payload EXP-F-EQUAL đã validate — nối vào CreateExpenseInput (fund_flow_allocated + split đều). */
export interface ExpFEqualPayload {
  readonly fundId: string;
  readonly name: string;
  /** STRING số nguyên dương minor (B1 — service parse → BigInt). */
  readonly amountMinor: string;
  /**
   * source=fund_flow_allocated, splitRule=equal, participants = NGƯỜI GÁNH (KHÔNG shareMinor —
   * `computeShares` chia đều; KHÔNG fundPortionMinor — contract cấm với allocated).
   */
  readonly split: ExpFormSplit;
}

/**
 * Dựng chuỗi field EXP-F-EQUAL. `bearers` = multi_choice trên options thành viên THẬT (service nạp theo
 * quỹ đã chọn — hydrateSpec). Immutable (copy options).
 *
 * MẶC ĐỊNH "chia đều cho TẤT CẢ": service prefill `bearers` = MỌI thành viên active (đúng nghĩa usecase);
 * user được phép THU HẸP (bỏ chọn ai đó) — lúc đó vẫn là chia đều trên tập đã chọn.
 */
export function buildExpFEqualFields(
  fundOptions: readonly SlotOption[],
  memberOptions: readonly SlotOption[],
): SlotFieldSpec[] {
  return [
    { key: "fundId", type: "fund_picker", required: true, options: [...fundOptions] },
    { key: "name", type: "text", required: true },
    { key: "amount", type: "money", required: true },
    {
      key: EXP_F_EQUAL_BEARERS_KEY,
      type: "multi_choice",
      required: true,
      options: [...memberOptions],
    },
  ];
}

/** Ném DomainError REQUIRED_FIELD_MISSING cho field bắt buộc còn trống. */
function assertPresent(value: string | null | undefined, field: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new DomainError("REQUIRED_FIELD_MISSING", `Thiếu thông tin bắt buộc: ${field}.`);
  }
  return value.trim();
}

/**
 * Gom khối split EXP-F-EQUAL từ tập NGƯỜI GÁNH đã chốt: source=fund_flow_allocated + splitRule=equal +
 * participants = [{groupMemberId}] (KHÔNG shareMinor — engine chia; KHÔNG payerMemberId — quỹ trả 100%,
 * contract cấm; KHÔNG fundPortionMinor — service tự cân bằng). Đây là SEAM nối `ExpenseService.create`.
 */
export function buildExpFEqualSplit(bearerMemberIds: readonly string[]): ExpFormSplit {
  return {
    source: EXP_SOURCE_ALLOCATED,
    splitRule: "equal",
    participants: bearerMemberIds.map((groupMemberId) => ({ groupMemberId })),
  };
}

/**
 * Validate CẢ BỘ formState EXP-F-EQUAL → ExpFEqualPayload chuẩn hoá (fail-fast, KHÔNG partial).
 *
 * Thứ tự chốt (tất-định):
 *  1. fundId có + ∈ options (chống IDOR quỹ lạ ở LỚP domain — service re-verify RBAC thật).
 *  2. name non-empty (trim) + ≤ trần độ dài (mirror HTTP .max(200)).
 *  3. amount money minor no-float (validateFieldValue) + >0 + ≤ trần BIGINT (B1).
 *  4. bearers ≥1 người + MỌI người ∈ options (IDOR thành viên lạ) + KHÔNG lặp (lặp ⇒ gánh 2 suất = sai tiền).
 * Ném DomainError (map 400) cho MỌI vi phạm.
 */
export function validateExpFEqualForm(
  formState: FormState,
  fundOptions: readonly SlotOption[],
  memberOptions: readonly SlotOption[],
): ExpFEqualPayload {
  // 1) fundId ∈ options (domain-level IDOR guard).
  const fundId = assertPresent(formState.fundId, "fundId");
  const allowedFundIds = new Set(fundOptions.map((o) => o.value));
  if (!allowedFundIds.has(fundId)) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      "Quỹ được chọn không nằm trong danh sách quỹ của bạn.",
    );
  }

  // 2) name non-empty + ≤ trần độ dài.
  const name = assertPresent(formState.name, "name");
  if (name.length > EXP_ADHOC_NAME_MAX_LENGTH) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `Tên khoản chi quá dài (tối đa ${EXP_ADHOC_NAME_MAX_LENGTH} ký tự).`,
    );
  }

  // 3) amount money minor no-float + >0 + ≤ trần BIGINT.
  const amountRaw = assertPresent(formState.amount, "amount");
  const amountValidation = validateFieldValue(
    { key: "amount", type: "money", required: true },
    amountRaw,
  );
  if (!amountValidation.ok) {
    throw new DomainError("LEDGER_AMOUNT_NOT_POSITIVE", amountValidation.message);
  }
  const amount = BigInt(amountValidation.value);
  if (amount <= 0n) {
    throw new DomainError("LEDGER_AMOUNT_NOT_POSITIVE", "Số tiền phải lớn hơn 0.");
  }
  if (amount > EXP_ADHOC_PG_BIGINT_MAX) {
    throw new DomainError("LEDGER_AMOUNT_NOT_POSITIVE", "Số tiền vượt quá giới hạn cho phép.");
  }

  // 4) bearers: ≥1 + ∈ options + KHÔNG lặp. multi_choice đã chốt "mọi phần ∈ options" (IDOR); ở đây
  //    chốt thêm KHÔNG-LẶP (validateFieldValue không dedupe — lặp ⇒ một người gánh 2 suất = sai tiền).
  const bearersRaw = assertPresent(formState[EXP_F_EQUAL_BEARERS_KEY], EXP_F_EQUAL_BEARERS_KEY);
  const bearersValidation = validateFieldValue(
    {
      key: EXP_F_EQUAL_BEARERS_KEY,
      type: "multi_choice",
      required: true,
      options: [...memberOptions],
    },
    bearersRaw,
  );
  if (!bearersValidation.ok) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      "Người gánh khoản chi không nằm trong danh sách thành viên của nhóm.",
    );
  }
  const bearerIds = bearersValidation.value.split(",").map((p) => p.trim()).filter(Boolean);
  if (bearerIds.length === 0) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      "Cần ít nhất một người gánh khoản chi.",
    );
  }
  const seen = new Set<string>();
  for (const id of bearerIds) {
    if (seen.has(id)) {
      throw new DomainError(
        "REQUIRED_FIELD_MISSING",
        "Thành viên bị lặp trong danh sách người gánh.",
      );
    }
    seen.add(id);
  }

  return {
    fundId,
    name,
    amountMinor: amountValidation.value,
    split: buildExpFEqualSplit(bearerIds),
  };
}
